import React, { ReactNode } from 'react';
import { computed, makeObservable } from 'mobx';
import { observer } from 'mobx-react';

import { ComponentBase } from '../../../shared/ComponentBase.js';
import { InjectedStyledComponent } from '../../../shared/InjectedStyledComponent.js';
import Tooltip from '../Tooltip/Tooltip.js';
import Translate from '../Translate/Translate.js';

import { IListConfiguration, IListProps, IListState } from './models/index.js';
import Content from './styledComponents/Content.js';
import FirstContent from './styledComponents/FirstContent.js';
import Heading from './styledComponents/Heading.js';
import LastContent from './styledComponents/LastContent.js';
import ListItem from './styledComponents/ListItem.js';
import ListItemWrapper from './styledComponents/ListItemWrapper.js';
import MultipartListHeading from './styledComponents/MultipartListHeading.js';
import MultipartListItem from './styledComponents/MultipartListItem.js';
import OrderedList from './styledComponents/OrderedList.js';
import Placeholder from './styledComponents/Placeholder.js';
import UnorderedList from './styledComponents/UnorderedList.js';
import Wrapper from './styledComponents/Wrapper.js';
import { LIST_CATEGORY_KEY, LIST_COMPONENT_KEY } from './List.meta.js';

export class List extends ComponentBase<IListProps, IListState, IListConfiguration> {
	categoryKey = LIST_CATEGORY_KEY;
	componentKey = LIST_COMPONENT_KEY;

	constructor(props: IListProps) {
		super(props);

		makeObservable(this, {
			blankDataOptions: computed,
			isSingleItemList: computed,
			listType: computed,
			heading: computed,
			items: computed,
		});
	}

	getItemValue(item: ReactNode) {
		if (typeof item === 'string') {
			return <Translate>{item}</Translate>;
		}
		return item;
	}

	getItemPart(index: number, length: number) {
		const { itemStyle = 'default' } = this.props;
		if (itemStyle === 'justified') {
			if (index === 0) {
				return FirstContent;
			}
			if (index === length - 1) {
				return LastContent;
			}
		}
		return Content;
	}

	isMultipartItem(items: ReactNode | ReactNode[]) {
		return Array.isArray(items) && items.length > 1;
	}

	getItemContent(items: ReactNode | ReactNode[]) {
		if (!Array.isArray(items) || items.length < 2) {
			return (
				<ListItem>
					<Content>{this.getItemValue(items)}</Content>
				</ListItem>
			);
		}
		return (
			<MultipartListItem>
				{items.map((item: ReactNode, index: number) => {
					// eslint-disable-next-line @typescript-eslint/naming-convention
					const ItemPart = this.getItemPart(index, items.length);
					return <ItemPart key={index}>{this.getItemValue(item)}</ItemPart>;
				})}
			</MultipartListItem>
		);
	}

	get blankDataOptions() {
		const { blankDataOptions = {} } = this.props;
		const defaults = {
			showBlank: true,
			placeholder: 'No list data available',
		};
		return Object.assign(defaults, blankDataOptions);
	}
	get isSingleItemList(): boolean {
		const { items } = this.props;
		return !!(items && items.length === 1);
	}

	get listType() {
		const { type = 'unordered' } = this.props;
		if (type === 'ordered') {
			return OrderedList;
		}
		return UnorderedList;
	}

	get heading() {
		const { heading, headingTooltip } = this.props;

		if (Array.isArray(heading)) {
			return (
				<Heading data-multipart={true}>
					{heading.map((headingPart: string, index: number) => {
						return (
							<MultipartListHeading key={`heading_${index}`}>
								<Translate>{headingPart}</Translate>
								{headingTooltip && index === 0 && <Tooltip {...headingTooltip} />}
							</MultipartListHeading>
						);
					})}
				</Heading>
			);
		}

		return (
			heading && (
				<Heading>
					<Translate>{heading}</Translate>
					{headingTooltip && <Tooltip {...headingTooltip} />}
				</Heading>
			)
		);
	}

	get items() {
		const { items = [], type, ...props } = this.props;
		// eslint-disable-next-line @typescript-eslint/naming-convention
		const ListType = this.listType;
		const hasItems = items && items.length > 0;
		return hasItems ? (
			<ListType {...props}>
				{items.map((item, index) => (
					<ListItemWrapper
						key={index}
						data-multipart={this.isMultipartItem(item)}
						data-single-item={this.isSingleItemList}
					>
						{this.getItemContent(item)}
					</ListItemWrapper>
				))}
			</ListType>
		) : (
			<Placeholder>
				<Translate>{this.blankDataOptions.placeholder}</Translate>
			</Placeholder>
		);
	}
	render() {
		const { className } = this.props;

		return (
			<Wrapper className={className}>
				{this.heading}
				{this.items}
			</Wrapper>
		);
	}
}

export default InjectedStyledComponent(observer(List), LIST_CATEGORY_KEY, LIST_COMPONENT_KEY);
